import React, { useState, useEffect } from 'react';
import { Container, Typography, Paper, List, ListItem, ListItemText, ListItemAvatar, Avatar, IconButton, Box, Badge, CircularProgress, Snackbar, Alert, Button } from '@mui/material';
import { db, auth } from '../firebase';
import { collection, getDocs, doc, updateDoc, deleteDoc, Timestamp, query, where, orderBy, getDoc, increment, addDoc } from 'firebase/firestore';
import { Link } from 'react-router-dom';

import AddIcon from '@mui/icons-material/Add';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import CommentIcon from '@mui/icons-material/Comment';
import PersonIcon from '@mui/icons-material/Person';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

function Announcements() {
  const [announcements, setAnnouncements] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(user => {
      setUser(user);
      if (user) fetchData(user.uid);
      else setLoading(false);
    });
    return unsubscribe;
  }, []);

  const fetchData = async (userId) => {
    try {
      const announcementsQuery = query(
        collection(db, 'announcements'),
        orderBy('date', 'desc')
      );
      const snapshot = await getDocs(announcementsQuery);

      const list = await Promise.all(snapshot.docs.map(async docSnap => {
        const data = docSnap.data();
        const authorSnap = data.authorId ? await getDoc(doc(db, 'users', data.authorId)) : null;
        const author = authorSnap?.exists() ? authorSnap.data() : { firstName: 'Inconnu', lastName: '' };

        let liked = false;
        if (userId) {
          const likeQuery = query(
            collection(db, 'likes'),
            where('announcementId', '==', docSnap.id),
            where('userId', '==', userId)
          );
          const likeSnap = await getDocs(likeQuery);
          liked = !likeSnap.empty;
        }

        return {
          id: docSnap.id,
          ...data,
          date: data.date?.toDate() || new Date(),
          authorName: `${author.firstName} ${author.lastName}`,
          liked
        };
      }));

      setAnnouncements(list);
    } catch (err) {
      console.error("Erreur chargement annonces :", err);
      setSnackbar({ open: true, message: 'Erreur de chargement', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    return date?.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }) || 'Date inconnue';
  };

  const handleLike = async (announcementId) => {
    if (!user) {
      setSnackbar({ open: true, message: 'Connectez-vous pour aimer', severity: 'error' });
      return;
    }

    try {
      const likeQuery = query(
        collection(db, 'likes'),
        where('announcementId', '==', announcementId),
        where('userId', '==', user.uid)
      );
      const likeSnap = await getDocs(likeQuery);

      if (!likeSnap.empty) {
        const likeDoc = likeSnap.docs[0];
        await deleteDoc(doc(db, 'likes', likeDoc.id));
        await updateDoc(doc(db, 'announcements', announcementId), {
          likesCount: increment(-1)
        });
        updateAnnouncementState(announcementId, -1, 'like');
      } else {
        await addDoc(collection(db, 'likes'), {
          announcementId,
          userId: user.uid,
          date: Timestamp.now()
        });
        await updateDoc(doc(db, 'announcements', announcementId), {
          likesCount: increment(1)
        });
        updateAnnouncementState(announcementId, 1, 'like');
      }
    } catch (err) {
      console.error("Erreur like :", err);
      setSnackbar({ open: true, message: "Erreur lors du like", severity: "error" });
    }
  };

  const updateAnnouncementState = (id, delta, type) => {
    setAnnouncements(prev =>
      prev.map(a => {
        if (a.id === id) {
          if (type === 'like') {
            return {
              ...a,
              likesCount: a.likesCount + delta,
              liked: delta > 0
            };
          }
        }
        return a;
      })
    );
  };

  const handleDelete = async (id) => {
    try {
      await deleteDoc(doc(db, 'announcements', id));
      setAnnouncements(prev => prev.filter(a => a.id !== id));
      setSnackbar({ open: true, message: 'Annonce supprimée', severity: 'success' });
    } catch (err) {
      console.error("Erreur suppression :", err);
      setSnackbar({ open: true, message: "Erreur suppression", severity: "error" });
    }
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress size={60} />
      </Container>
    );
  }

  return (
    <Container>
      <Typography variant="h4" gutterBottom>Annonces</Typography>

      {user && (
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          component={Link}
          to="/announcements/new"
          sx={{ mb: 3 }}
        >
          Nouvelle Annonce
        </Button>
      )}

      <List>
        {announcements.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography>Aucune annonce disponible</Typography>
          </Paper>
        ) : (
          announcements.map(announcement => (
            <ListItem key={announcement.id} component={Paper} sx={{ mb: 2, p: 2 }}>
              <ListItemAvatar>
                <Avatar><PersonIcon /></Avatar>
              </ListItemAvatar>
           <ListItemText
  primary={
    <Link to={`/announcements/${announcement.id}`} style={{ textDecoration: 'none', color: '#1976d2' }}>
      {announcement.title}
    </Link>
  }
                secondary={
                  <>
                    <Typography variant="body2">{announcement.description.slice(0, 100)}...</Typography>
                    <Typography variant="caption" color="textSecondary">
                      Par {announcement.authorName} • {formatDate(announcement.date)}
                    </Typography>
                  </>
                }
              />
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton onClick={() => handleLike(announcement.id)}>
                  <Badge badgeContent={announcement.likesCount} color="primary">
                    {announcement.liked ? <FavoriteIcon color="error" /> : <FavoriteBorderIcon />}
                  </Badge>
                </IconButton>
                <IconButton>
                  <Badge badgeContent={announcement.commentsCount} color="primary">
                    <CommentIcon />
                  </Badge>
                </IconButton>
                {user && user.uid === announcement.authorId && (
                  <>
                    <IconButton component={Link} to={`/announcements/edit/${announcement.id}`}>
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(announcement.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </>
                )}
              </Box>
            </ListItem>
          ))
        )}
      </List>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Container>
  );
}

export default Announcements;
